import React from 'react';
import { Box, Container, Typography, Grid, Paper, useTheme, Divider } from '@mui/material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import SchoolIcon from '@mui/icons-material/School';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';
import lightModeAboutImage from '../assets/digispark_asset_2.png';
import darkModeAboutImage from '../assets/digispark_asset_1.png';

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  position: 'relative',
  '&:after': {
    content: '""',
    display: 'block',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  }
}));

const ValueCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: theme.shadows[10],
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  borderRadius: '50%',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const About = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  // Determine which about image to use based on theme mode
  const aboutImage = theme.palette.mode === 'dark' ? darkModeAboutImage : lightModeAboutImage;

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          py: 10, 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)' 
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionTitle variant="h3">
                {t('about.title')}
              </SectionTitle>
              <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}>
                {t('about.subtitle')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('about.introDescription1')}
              </Typography>
              <Typography variant="body1">
                {t('about.introDescription2')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={aboutImage}
                alt="About Digispark Foundation"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionTitle variant="h3" align="center">
              {t('about.missionVisionTitle')}
            </SectionTitle>
          </Box>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderTop: `4px solid ${theme.palette.primary.main}`
                }}
              >
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('about.missionTitle')}
                </Typography>
                <Typography variant="body1">
                  {t('about.missionText')}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderTop: `4px solid ${theme.palette.secondary.main}`
                }}
              >
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('about.visionTitle')}
                </Typography>
                <Typography variant="body1">
                  {t('about.visionText')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box 
        sx={{ 
          py: 10, 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)' 
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <SectionTitle variant="h3" align="center">
              {t('about.coreValuesTitle')}
            </SectionTitle>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('about.coreValuesSubtitle')}
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <ValueCard elevation={2}>
                <IconWrapper>
                  <SchoolIcon fontSize="large" />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('about.educationTitle')}
                </Typography>
                <Typography variant="body2">
                  {t('about.educationText')}
                </Typography>
              </ValueCard>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <ValueCard elevation={2}>
                <IconWrapper>
                  <AgricultureIcon fontSize="large" />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('about.innovationTitle')}
                </Typography>
                <Typography variant="body2">
                  {t('about.innovationText')}
                </Typography>
              </ValueCard>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <ValueCard elevation={2}>
                <IconWrapper>
                  <GroupsIcon fontSize="large" />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('about.communityTitle')}
                </Typography>
                <Typography variant="body2">
                  {t('about.communityText')}
                </Typography>
              </ValueCard>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <ValueCard elevation={2}>
                <IconWrapper>
                  <PublicIcon fontSize="large" />
                </IconWrapper>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t('about.sustainabilityTitle')}
                </Typography>
                <Typography variant="body2">
                  {t('about.sustainabilityText')}
                </Typography>
              </ValueCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* History Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <SectionTitle variant="h3" align="center" sx={{ mb: 6 }}>
            {t('about.historyTitle')}
          </SectionTitle>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              {t('about.foundedTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('about.foundedText')}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Box>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
              {t('about.growingImpactTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('about.growingImpactText')}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 